// src/pages/auth/Login.tsx
import { useState, FormEvent, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Mail, Lock, Scale } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import toast from 'react-hot-toast'

export default function Login() {
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [mostrarSenha, setMostrarSenha] = useState(false)
  const [loading, setLoading] = useState(false)
  
  const { login, isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()

  // Se já está autenticado, redireciona para home
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    
    if (!email || !senha) {
      toast.error('Preencha todos os campos')
      return
    }

    setLoading(true)
    
    try {
      await login(email, senha)
      toast.success('Login realizado com sucesso!')
      // O redirecionamento é feito pelo AuthContext
    } catch (error: any) {
      console.error('Erro no login:', error)
      const message = error.response?.data?.error || error.message || 'Erro ao fazer login'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  // Enquanto carrega, mostra loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" 
           style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ed 100%)' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FFB800] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    )
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ed 100%)' }}
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div 
            className="w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: '#FFB800' }}
          >
            <Scale className="w-10 h-10" style={{ color: '#0F1B3D' }} />
          </div>
          <h1 
            className="text-2xl font-bold"
            style={{ color: '#0F1B3D', fontFamily: 'Poppins, sans-serif' }}
          >
            SYSTELOS MONITOR
          </h1>
          <p className="text-gray-500 mt-1">Monitoramento de Comunicações DJE</p>
        </div>

        {/* Card de Login */}
        <div 
          className="bg-white rounded-2xl p-8"
          style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}
        >
          <h2 
            className="text-xl font-semibold mb-6"
            style={{ color: '#0F1B3D' }}
          >
            Entrar
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB800] focus:border-transparent outline-none transition-all"
                  placeholder="seu@email.com"
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Senha */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={mostrarSenha ? 'text' : 'password'}
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  className="w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB800] focus:border-transparent outline-none transition-all"
                  placeholder="••••••••"
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setMostrarSenha(!mostrarSenha)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {mostrarSenha ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Botão Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                backgroundColor: '#FFB800', 
                color: '#0F1B3D',
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-[#0F1B3D] border-t-transparent rounded-full animate-spin"></div>
                  Entrando...
                </span>
              ) : (
                'Entrar'
              )}
            </button>
          </form>

          {/* Link Esqueci Senha */}
          <div className="mt-6 text-center">
            <button 
              type="button"
              className="text-sm text-gray-500 hover:text-[#0F1B3D] transition-colors"
              onClick={() => toast('Funcionalidade em desenvolvimento', { icon: 'ℹ️' })}
            >
              Esqueci minha senha
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-400 text-sm mt-8">
          © 2026 SYSTELOS - Sistemas com Propósito
        </p>
      </div>
    </div>
  )
}

